import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorPage } from './ErrorPage'
import { Button } from './index'

/**
 * Catches a render error anywhere beneath it and shows a designed page.
 *
 * Without one, React unmounts the whole tree on an uncaught error and the
 * document is left blank — the single worst state to be in, because it looks
 * identical to a failed load and tells the person nothing. This is the only
 * class component in the app: error boundaries have no hook equivalent.
 *
 * Reload rather than "try again": the error escaped render, so the tree's state
 * is not trustworthy, and re-rendering the same state usually reproduces the
 * same crash. A reload is the honest offer.
 *
 * A boundary catches RENDER errors only — not an event handler, not an async
 * rejection, not a failed query. Those surface where they happen, as a `Notice`
 * inside a page that still works.
 *
 * `resetKey` is what somebody moved on to — a pathname where one of these wraps
 * a route. A caught error is otherwise terminal, so a boundary around part of
 * the app would keep showing the failure after the person navigated away from
 * the screen that raised it, and the only way out of a working app would be a
 * reload. Changing it clears the error without remounting the children, which
 * is why this is a prop rather than a `key`.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Nothing collects these yet, so the console is the only record — and a
    // silent boundary is worse than no boundary, because the crash stops being
    // visible to whoever has to fix it.
    console.error('Unhandled render error', error, info.componentStack)
  }

  componentDidUpdate(previous: { resetKey?: string }) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false })
    }
  }

  render() {
    if (!this.state.failed) return this.props.children
    return (
      <ErrorPage
        code="500"
        title="Something went wrong on this page."
        actions={
          <Button onClick={() => window.location.reload()}>Reload</Button>
        }
      >
        The page stopped before it finished drawing. Reloading usually clears
        it; if it keeps happening, the address you came from is worth reporting.
      </ErrorPage>
    )
  }
}
